import moment from "moment";
import CryptoJS from "crypto-js";
import { skuRegex, snackConst } from "../constants/Variables";

const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

const handleOpenSnackBar = (type, message) => {
  snackConst.snackRef.isOpenSnack({
    snackType: type,
    snackMessage: message,
  });
};

const encryptPassword = (password) => {
  const encrypted = CryptoJS.AES.encrypt(password, ENCRYPTION_KEY).toString();
  return encrypted;
};

const formattedDate = (date, includeTime) => {
  const format = includeTime ? "DD/MM/YYYY h:mm a" : "DD/MM/YYYY";
  return moment(date || new Date()).format(format);
};

const formattedTime = (date, isAmPm) => {
  const format = isAmPm ? "h:mm a" : "h:mm";
  return moment(date || new Date()).format(format);
};

function calculatePackageDuration(startDate, endDate) {
  const currentDate = new Date();

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (currentDate < start) {
    console.log("The package hasn't started yet.");
    return "Not start yet";
  }

  const timeDiff = end - currentDate;

  if (timeDiff < 0) {
    console.log("The package has already expired.");
    return "Expired";
  }

  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor(timeDiff / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

  return { days, hours, minutes, seconds };
}

const getCurrentWeek = () => {
  const current = new Date();
  const week = [];

  // Set date to Monday of the current week
  current.setDate(current.getDate() - current.getDay() + 1);
  for (let i = 0; i < 7; i++) {
    week.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return week;
};

const getStatusBackgroundColor = (status) => {
  switch (status.toLowerCase()) {
    case "complete":
      return { backgroundColor: "#0DBE89" };
    case "return":
      return { backgroundColor: "#F85A5A" };
    case "on the way":
      return { backgroundColor: "#FFEA2F" };
    default:
      return { backgroundColor: "#FFF" };
  }
};

function getGrade(marks) {
  let grade = "";

  if (marks >= 90) {
    grade = "A+";
  } else if (marks >= 80) {
    grade = "A";
  } else if (marks >= 70) {
    grade = "B";
  } else if (marks >= 60) {
    grade = "C";
  } else if (marks >= 50) {
    grade = "D";
  } else {
    grade = "F";
  }

  return grade;
}

function getGradeAndRemarks(marks) {
  let grade = "";
  let remarks = "";

  if (marks >= 90) {
    grade = "A+";
    remarks = "Excellent";
  } else if (marks >= 80) {
    grade = "A";
    remarks = "Very Good";
  } else if (marks >= 70) {
    grade = "B";
    remarks = "Good";
  } else if (marks >= 60) {
    grade = "C";
    remarks = "Satisfactory";
  } else if (marks >= 50) {
    grade = "D";
    remarks = "Needs Improvement";
  } else {
    grade = "F";
    remarks = "Fail";
  }

  return { grade, remarks };
}

function calculateRemainingDays(endDate) {
  // Get today's date
  const today = new Date();
  // Create a date object from the endDate string
  const end = new Date(endDate);

  // Calculate the difference in milliseconds
  const differenceInMillis = end - today;

  // Convert milliseconds to days
  const daysRemaining = Math.ceil(differenceInMillis / (1000 * 60 * 60 * 24));

  // If days remaining is negative, return 0 (or handle as needed)
  return daysRemaining < 0 ? 0 : daysRemaining;
}

const getServiceDuration = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const durationMs = endDate - startDate;
  const durationMinutes = Math.floor(durationMs / 60000);
  const hours = Math.floor(durationMinutes / 60);
  const minutes = durationMinutes % 60;
  if (hours === 0) {
    return `${minutes} mins`;
  } else {
    return `${hours} hrs : ${minutes} mins`;
  }
};

const convertMinutesToHours = (minutes) => {
  if (minutes < 0) return "Invalid input";

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const hoursPart = hours > 0 ? `${hours} hour${hours > 1 ? "s" : ""}` : "";
  const minutesPart =
    remainingMinutes > 0
      ? `${remainingMinutes} minute${remainingMinutes > 1 ? "s" : ""}`
      : "";

  return `${hoursPart}${hoursPart && minutesPart ? " " : ""
    }${minutesPart}`.trim();
};

// Helper function to get image dimensions
const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const capitalizeText = (text) => {
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const roundTo2Decimal = (value) => Math.floor(value * 100) / 100;

const renderFinalProductPrice = (p, d, m) => {
  const price = Number(p) || 0; // Cost Price
  const discount = Number(d) || 0; // Discount in amount
  const profitMargin = Number(m) || 0; // Profit %

  // Calculate profit amount
  const profitAmount = (price * profitMargin) / 100;

  // Selling price
  let finalPrice = price + profitAmount - discount;

  // 3 decimal precision
  finalPrice = roundTo2Decimal(finalPrice);

  return finalPrice;
};

const calculateVat = (p, v) => {
  const price = Number(p) || 0; // SP
  const vat = Number(v) || 0;

  let vatAmount = price * (vat / 100);

  vatAmount = roundTo2Decimal(vatAmount);
  return vatAmount;
};

// const calculateLossAmount = (p, d, pm) => {
//   const price = Number(p) || 0;
//   const discount = Number(d) || 0;
//   const profitMargin = Number(pm) || 0;

//   // SP = CP + profit - discount
//   const sellingPrice = price + calculateProfitMargin(price, profitMargin) - discount;

//   // Loss (negative = profit)
//   let lossAmount = price - sellingPrice;

//   return roundTo2Decimal(lossAmount);
// };

const calculateLossAmount = (p, d, pm) => {
  const price = Number(p) || 0;
  const discount = Number(d) || 0;
  const profitMargin = Number(pm) || 0;

  const finalPrice =
    price + calculateProfitMargin(price, profitMargin) - discount;
  let lossAmount = finalPrice - price;
  lossAmount = roundTo2Decimal(lossAmount);
  return lossAmount;
};

const calculateProfitMargin = (p, pm) => {
  const price = Number(p) || 0;
  const profitMargin = Number(pm) || 0;

  let profitAmount = price * (profitMargin / 100);

  return roundTo2Decimal(profitAmount);
};

const calculateDiscountPercentage = (fp, dp) => {
  // console.log(fp, dp, "sadjasdjsajdsajhdhjsadjhsajhdsahj");
  // const finalPrice = Number(fp) || 0; // Selling price before discount
  // const discountPrice = Number(dp) || 0; // Discount amount

  // if (finalPrice === 0) return 0;

  // let discountPercentage = (discountPrice / finalPrice) * 100;

  // return roundTo2Decimal(discountPercentage);

  const finalPrice = Number(fp) || 0; // 105
  const discount = Number(dp) || 0; // 5

  if (discount === 0) return 0;

  // ✅ Calculate price BEFORE discount
  const priceBeforeDiscount = finalPrice + discount; // 105 + 5 = 110

  if (priceBeforeDiscount === 0) return 0;

  // ✅ Correct formula
  let discountPercentage = (discount / priceBeforeDiscount) * 100;
  // = (5 / 110) × 100 = 4.545%

  return roundTo2Decimal(discountPercentage);
};


function calculateOverAllPurchasePrice(selectedPackageProduct = []) {
  let purchasePrice = 0;
  selectedPackageProduct?.forEach((product) => {
    const productPrice = product.price || 0;
    purchasePrice += productPrice;
  });
  return purchasePrice;
}

function calculateOverAllFinalAmount(selectedPackageProduct = []) {
  let finalAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productPrice = product.finalPrice || 0;
    finalAmount += productPrice;
  });
  return finalAmount;
}

function calculateOverAllDiscount(selectedPackageProduct = []) {
  let discount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productDiscount = product.discount || 0;
    discount += productDiscount;
  });
  return discount;
}

function calculateOverAllProfitMargin(selectedPackageProduct = []) {
  let profitMargin = 0;
  selectedPackageProduct?.forEach((product) => {
    const productProfitMargin = product.profitMargin || 0;
    profitMargin += productProfitMargin;
  });
  return profitMargin;
}

function calculateAverageProfitMargin(selectedPackageProduct = []) {
  let totalPurchasePrice = 0;
  let totalFinalPrice = 0;

  selectedPackageProduct?.forEach((product) => {
    totalPurchasePrice += product.price || 0;
    totalFinalPrice += product.finalPrice || 0;
  });

  if (totalPurchasePrice === 0) return 0;

  const averageProfitMargin =
    ((totalFinalPrice - totalPurchasePrice) / totalPurchasePrice) * 100;

  return roundTo2Decimal(averageProfitMargin);
}

function calculateOverAllEarnAmount(selectedPackageProduct = []) {
  let earnAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productEarnAmount = product.earnAmount || 0;
    earnAmount += productEarnAmount;
  });
  return earnAmount;
}

const calculateOverAllLossAmount = (selectedPackageProduct = []) => {
  let lossAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productLossAmount = product.lossAmount || 0;
    lossAmount += productLossAmount;
  });
  return lossAmount;
};

const calculateOverAllVatAmount = (selectedPackageProduct = []) => {
  let vatAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productVatAmount = product.vatAmount || 0;
    vatAmount += productVatAmount;
  });
  return vatAmount;
};

const getCityFromPlaces = (place) => {
  let city = "";
  if (place.address_components) {
    const cityObj =
      place.address_components.find((c) => c.types.includes("locality")) ||
      place.address_components.find((c) =>
        c.types.includes("administrative_area_level_2")
      ) ||
      place.address_components.find((c) =>
        c.types.includes("administrative_area_level_1")
      );

    city = cityObj ? cityObj.long_name : "";
  }
  return city;
};


const getCountryFromPlaces = (place) => {
  let country = "";
  if (place.address_components) {
    const countryObj = place.address_components.find((c) => c.types.includes("country"));
    country = countryObj ? countryObj.long_name : "";
  }
  return country;
};

const calculateSupplierTotalOrderPrice = (product=[]) => {
  let a = 0;
  product.map((v) => {
    a += (v?.pricing?.finalPrice || 0) * v?.selectedQty;
  });
  return roundTo2Decimal(a);
};

const calculateProductOrderVatAmount = (selectedPackageProduct = []) => {
  let vatAmount = 0;
  selectedPackageProduct?.forEach((product) => {
    const productVatAmount = product?.pricing?.originalVatAmount * product?.selectedQty || 0;
    vatAmount += productVatAmount;
  });
  return roundTo2Decimal(vatAmount);
};

const decodeHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.documentElement.textContent;
};

const calculateProductFinalPrice = (p, d, m) => {
  const price = Number(p) || 0; // Cost Price
  const discount = Number(d) || 0; // Discount in amount
  const profitMargin = Number(m) || 0; // Profit %

  // Calculate profit amount
  const profitAmount = (price * profitMargin) / 100;

  // Selling price
  let finalPrice = price + profitAmount - discount;

  // 3 decimal precision
  finalPrice = roundTo2Decimal(finalPrice);

  return finalPrice;
};

const overAllProductQuantity = (variants) => {
  let quantity = 0;
  variants.forEach((variant) => {
    quantity += variant.quantity;
  });
  return quantity;
};

const validateVariants = (variants) => {
  const inValidImage = variants.every((variant) => variant.images.length === 0);
  const inValidPrice = variants.every((variant) => variant.price === 0 || variant.price < 0 || isNaN(variant.price));
  const inValidQuantity = variants.every((variant) => variant.quantity === 0 || variant.quantity < 0 || isNaN(variant.quantity));
  const inValidProfitMargin = variants.every((variant) => variant.profitMargin < 0 || isNaN(variant.profitMargin));
  const inValidDiscount = variants.every((variant) => variant.discount < 0 || isNaN(variant.discount));
  const inValidSku = variants.every((variant) => variant.sku === "" || !skuRegex.test(variant.sku));

  const errors = [];

  if (inValidImage) errors.push("validation.variant.inValidImage");
  if (inValidPrice) errors.push("validation.variant.inValidPrice");
  if (inValidQuantity) errors.push("validation.variant.inValidQuantity");
  if (inValidProfitMargin) errors.push("validation.variant.inValidProfitMargin");
  if (inValidDiscount) errors.push("validation.variant.inValidDiscount");
  if (inValidSku) errors.push("validation.variant.inValidSku");
  return errors;
};

const getVariantLabel = (attributes, currentLanguage) => {
  if (!attributes || typeof attributes !== "object") {
    return "Selected variant";
  }

  const entries = Object.entries(attributes);
  if (entries.length === 0) return "Selected variant";

  return entries
    .map(
      ([key, val]) =>
        `${val?.label?.[currentLanguage] || val?.label || val?.value || "-"}`,
    )
    .join(", ");
};

export {
  formattedDate,
  formattedTime,
  calculatePackageDuration,
  getStatusBackgroundColor,
  getCurrentWeek,
  getGrade,
  getGradeAndRemarks,
  encryptPassword,
  handleOpenSnackBar,
  calculateRemainingDays,
  renderFinalProductPrice,
  getServiceDuration,
  convertMinutesToHours,
  getImageDimensions,
  calculateVat,
  calculateLossAmount,
  calculateProfitMargin,
  calculateDiscountPercentage,
  calculateOverAllPurchasePrice,
  calculateOverAllFinalAmount,
  calculateOverAllDiscount,
  calculateOverAllProfitMargin,
  calculateAverageProfitMargin,
  calculateOverAllEarnAmount,
  calculateOverAllLossAmount,
  calculateOverAllVatAmount,
  getCityFromPlaces,
  getCountryFromPlaces,
  roundTo2Decimal,
  calculateSupplierTotalOrderPrice,
  calculateProductOrderVatAmount,
  decodeHtml,
  calculateProductFinalPrice,
  overAllProductQuantity,
  validateVariants,
  getVariantLabel,
};
